import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Target, RotateCcw } from "lucide-react";

const STAGES = [
  { key: "PR_QUOT", label: "PR → Quot Email", hint: "From Purchase Request to Quotation email received" },
  { key: "PR_PO",   label: "PR → PO Shared",  hint: "From PR to Purchase Order shared with supplier" },
  { key: "PO_GR",   label: "PO Shared → GR",  hint: "From PO shared to Goods Receipt at warehouse" },
  { key: "PR_GR",   label: "PR → GR (Total)", hint: "End-to-end: PR until goods are received" },
];

export const SlaSettingsDialog = ({ open, onOpenChange, onSaved }) => {
  const [targets, setTargets] = useState({ PR_QUOT: 3, PR_PO: 5, PO_GR: 10, PR_GR: 18 });
  const [defaults, setDefaults] = useState({ PR_QUOT: 3, PR_PO: 5, PO_GR: 10, PR_GR: 18 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/settings/sla").then((r) => {
      setTargets(r.data.targets);
      setDefaults(r.data.defaults);
    }).catch(() => {});
  }, [open]);

  const set = (k, v) => setTargets((p) => ({ ...p, [k]: Math.max(1, Math.min(365, parseInt(v || "0", 10) || 1)) }));

  const resetDefaults = () => setTargets(defaults);

  const submit = async () => {
    setSaving(true);
    try {
      await api.put("/settings/sla", { targets });
      toast.success("SLA targets saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111D38] border-slate-700 text-slate-100 max-w-xl" data-testid="sla-settings-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100 text-xl flex items-center gap-2">
            <Target className="h-5 w-5 text-amber-400" /> SLA Target Settings
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Set the maximum acceptable lead time per stage. Dashboard SLA compliance updates automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {STAGES.map((s) => (
            <div key={s.key} data-testid={`sla-row-${s.key}`} className="grid grid-cols-[1fr_auto] gap-3 items-center p-3 rounded-lg border border-slate-700 bg-[#0B132B]/60">
              <div>
                <Label className="text-sm font-semibold text-slate-100">{s.label}</Label>
                <p className="text-[11px] text-slate-500 mt-0.5">{s.hint}</p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={365}
                  data-testid={`sla-input-${s.key}`}
                  value={targets[s.key] ?? ""}
                  onChange={(e) => set(s.key, e.target.value)}
                  className="bg-[#0B132B] border-slate-700 text-right h-9 w-20 font-mono text-amber-300 font-bold"
                />
                <span className="text-xs text-slate-400">days</span>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4 flex-row justify-between sm:justify-between">
          <Button
            variant="ghost"
            onClick={resetDefaults}
            data-testid="btn-sla-reset"
            className="text-slate-300 hover:text-amber-300 gap-1.5"
          >
            <RotateCcw className="h-4 w-4" /> Reset Defaults
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="btn-sla-save" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
              {saving ? "Saving..." : "Save Targets"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
